import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSample } from "../../api/client";
import { SampleDetail as SampleDetailT, SampleResultRow } from "../../types";
import DocumentsPanel from "../Documents/DocumentsPanel";

/** Read-only view of a single sample — its results vs. permit limits and any
 *  violations. Reached from the Coordinator's Pending-AO evidence panel
 *  ("View full sample →") and accessible to any authenticated reviewer
 *  (the backend scopes IU users to their own company). */
export default function SampleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sample, setSample] = useState<SampleDetailT | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    getSample(Number(id))
      .then(r => { if (active) setSample(r.data); })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  return (
    <div style={s.page}>
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <h1 style={s.headerTitle}>Sample {id ? `#${id}` : ""}</h1>
      </header>

      <div style={s.body}>
        {loading && <p style={s.muted}>Loading…</p>}
        {!loading && notFound && (
          <p style={s.muted}>Sample not found, or you don’t have access to it.</p>
        )}
        {!loading && sample && (
          <>
            <section style={s.card}>
              <div style={s.metaGrid}>
                <Meta label="Company"  value={sample.company_name ?? `#${sample.company_id}`} />
                <Meta label="Permit"   value={sample.permit_number ?? `#${sample.permit_id}`} />
                <Meta label="Sampled"  value={sample.sample_date} />
                <Meta label="Sampler"  value={sample.sampler_name ?? "—"} />
                <Meta label="Flow"     value={sample.flow_mgd != null ? `${sample.flow_mgd} MGD` : "—"} />
                <Meta label="Review"   value={<ReviewBadge status={sample.review_status} />} />
              </div>
              {sample.is_corrected && <div style={s.correctedTag}>Corrected sample</div>}
              {sample.review_comment && (
                <div style={s.comment}><strong>Reviewer note:</strong> {sample.review_comment}</div>
              )}
            </section>

            <section style={s.card}>
              {/* The lab report the results were imported from (view / download only here). */}
              <DocumentsPanel entityType="sample" entityId={sample.id} readOnly
                title={`Lab report (COA)${(sample as any).lab_job_id ? ` · job ${(sample as any).lab_job_id}` : ""}`}
                emptyText="No lab report on file for this sample." />
            </section>

            <section style={s.card}>
              <h2 style={s.h2}>Results</h2>
              {sample.results.length === 0 ? (
                <p style={s.muted}>No results recorded on this sample.</p>
              ) : (
                <table style={s.table}>
                  <thead><tr>
                    <th style={s.th}>Parameter</th>
                    <th style={s.thNum}>Measured</th>
                    <th style={s.thNum}>Limit</th>
                    <th style={s.th}>Status</th>
                  </tr></thead>
                  <tbody>
                    {sample.results.map(r => {
                      const { measured, limit, exceeds, hasLimit } = resultView(r);
                      return (
                        <tr key={r.id} style={exceeds ? s.rowBad : undefined}>
                          <td style={s.td}>{r.parameter_name}</td>
                          <td style={s.tdNum}>{measured}</td>
                          <td style={s.tdNum}>{limit}</td>
                          <td style={s.td}>
                            {r.is_monitor_report
                              ? <span style={s.badgeMR}>Monitor only</span>
                              : !hasLimit
                                ? <span style={s.badgeMR}>No limit</span>
                                : exceeds
                                  ? <span style={s.badgeBad}>Exceeds</span>
                                  : <span style={s.badgeOk}>Within limit</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>

            <section style={s.card}>
              <h2 style={s.h2}>Violations</h2>
              {sample.violations.length === 0 ? (
                <p style={s.ok}>✓ No violations associated with this sample.</p>
              ) : (
                <ul style={s.violList}>
                  {sample.violations.map(v => (
                    <li key={v.id} style={s.violItem}>
                      <strong>{v.parameter_name}</strong> · {v.violation_type.replace(/_/g, " ")}
                      {" · "}{v.violation_date}
                      {v.violation_severity && <span style={s.violSev}>{v.violation_severity}</span>}
                      {v.exceedance_percent != null && (
                        <span style={s.violExceed}>+{v.exceedance_percent.toFixed(1)}%</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={s.metaLabel}>{label}</div>
      <div style={s.metaValue}>{value}</div>
    </div>
  );
}

function ReviewBadge({ status }: { status: string }) {
  const map: Record<string, React.CSSProperties> = {
    reviewed: { background: "#c6f6d5", color: "#22543d" },
    pending:  { background: "#fefcbf", color: "#744210" },
    rejected: { background: "#fed7d7", color: "#822727" },
  };
  return <span style={{ ...s.reviewBadge, ...(map[status] ?? map.pending) }}>{status}</span>;
}

/** Decide a result's status by the basis that actually carries a limit
 *  (concentration preferred — the app's primary compliance notion), keeping the
 *  measured value on that same basis. A row exceeds if it breaks EITHER its
 *  concentration or its loading limit. Range limits (e.g. pH) compare min/max. */
function resultView(r: SampleResultRow):
    { measured: string; limit: string; exceeds: boolean; hasLimit: boolean } {
  if (r.is_range_limit) {
    const v = r.concentration_result;
    const hasLimit = r.min_value != null || r.max_value != null;
    const exceeds = v != null &&
      ((r.min_value != null && v < r.min_value) || (r.max_value != null && v > r.max_value));
    return { measured: v != null ? String(v) : "—",
             limit: `${r.min_value ?? "—"}–${r.max_value ?? "—"}`, exceeds, hasLimit };
  }

  const exceedsConc = !r.is_monitor_report && r.concentration_result != null
    && r.daily_max_concentration != null && r.concentration_result > r.daily_max_concentration;
  const exceedsLoad = !r.is_monitor_report && r.loading_result != null
    && r.daily_max_loading != null && r.loading_result > r.daily_max_loading;
  const exceeds = exceedsConc || exceedsLoad;

  if (r.daily_max_concentration != null) {
    return {
      measured: r.concentration_result != null ? `${r.concentration_result} mg/L` : "—",
      limit: `${r.daily_max_concentration} mg/L`, exceeds, hasLimit: true,
    };
  }
  if (r.daily_max_loading != null) {
    return {
      measured: r.loading_result != null ? `${r.loading_result} lbs/day` : "—",
      limit: `${r.daily_max_loading} lbs/day`, exceeds, hasLimit: true,
    };
  }
  // No numeric daily-max limit on this parameter — show whatever was reported.
  return {
    measured: r.concentration_result != null ? `${r.concentration_result} mg/L`
            : r.loading_result != null ? `${r.loading_result} lbs/day` : "—",
    limit: "—", exceeds: false, hasLimit: false,
  };
}

const s: Record<string, React.CSSProperties> = {
  page:        { minHeight: "100vh", background: "#f7fafc" },
  header:      { background: "#1a365d", color: "#fff", padding: "12px 24px",
                 display: "flex", alignItems: "center", gap: 16 },
  backBtn:     { background: "rgba(255,255,255,0.15)", color: "#fff", border: "none",
                 borderRadius: 5, padding: "6px 14px", cursor: "pointer", fontSize: 13 },
  headerTitle: { fontSize: 18, fontWeight: 700, margin: 0 },
  body:        { maxWidth: 900, margin: "0 auto", padding: 24 },
  card:        { background: "#fff", borderRadius: 8, padding: 20, marginBottom: 16,
                 boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  h2:          { fontSize: 15, fontWeight: 700, color: "#1a365d", marginTop: 0, marginBottom: 12 },
  metaGrid:    { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 },
  metaLabel:   { fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase",
                 letterSpacing: 0.3 },
  metaValue:   { fontSize: 15, color: "#1a202c", marginTop: 2 },
  reviewBadge: { display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12,
                 fontWeight: 600, textTransform: "capitalize" },
  correctedTag:{ marginTop: 12, display: "inline-block", background: "#bee3f8", color: "#2a4365",
                 padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 },
  comment:     { marginTop: 12, fontSize: 13, color: "#4a5568" },
  table:       { width: "100%", borderCollapse: "collapse" },
  th:          { textAlign: "left", fontSize: 12, color: "#718096", padding: "6px 8px",
                 borderBottom: "2px solid #e2e8f0" },
  thNum:       { textAlign: "right", fontSize: 12, color: "#718096", padding: "6px 8px",
                 borderBottom: "2px solid #e2e8f0" },
  td:          { fontSize: 13, padding: "8px", borderBottom: "1px solid #edf2f7" },
  tdNum:       { fontSize: 13, padding: "8px", borderBottom: "1px solid #edf2f7", textAlign: "right" },
  rowBad:      { background: "#fff5f5" },
  badgeOk:     { color: "#276749", fontWeight: 600, fontSize: 12 },
  badgeBad:    { color: "#c53030", fontWeight: 700, fontSize: 12 },
  badgeMR:     { color: "#718096", fontSize: 12 },
  violList:    { listStyle: "none", padding: 0, margin: 0 },
  violItem:    { fontSize: 13, padding: "8px 0", borderBottom: "1px solid #edf2f7" },
  violSev:     { marginLeft: 8, textTransform: "capitalize", color: "#975a16", fontWeight: 600 },
  violExceed:  { marginLeft: 8, color: "#c53030", fontWeight: 700 },
  muted:       { color: "#718096" },
  ok:          { color: "#276749", fontWeight: 600 },
};
